<script>
  // One Wants Board section: owns its fetch/pagination; the page passes the
  // scope/sort/filter and receives counts back (the API returns all three
  // counts with any section's first page — first responder wins).
  //
  // Rows are visually/behaviorally IDENTICAL to the search list rows (user
  // feedback: "Match the search list, replace owners with seekers, no badges,
  // no heart count"). CardRow normally lives inside CardCollection's
  // `.cc-list`, which defines --cc-grid-cols once for CardRow's `.cc-row` grid.
  // WantsSection replicates that minimal wrapper here (same class names, same
  // track) rather than restructuring CardCollection for a second consumer;
  // see .superpowers/sdd/polish-report.md "Row redesign" for the
  // full rationale.
  import CardRow from '../cardcollection/CardRow.svelte';
  import CardHolders from '../cardcollection/CardHolders.svelte';
  import { fetchBoardSection, fetchBoardDetail } from '../../lib/services/wants-board-api.js';
  import { boardRowToItem } from '../../lib/logic/wants-board-logic.js';

  let {
    section,
    title,
    scopeParams,
    sort = 'most-wanted',
    q = '',
    count = null,
    currencySymbol = '$',
    onCounts,
    onCardClick,
  } = $props();

  let items = $state([]);
  let nextCursor = $state(null);
  let loading = $state(false);
  let loadingMore = $state(false);
  let failed = $state(false);

  // Row-click expand state, mirroring CardCollection's expandedKeys but with a
  // per-row lazy-fetched detail cache (the board's detail endpoint is a
  // separate request from the section list, unlike search which already has
  // full holder data on the row).
  let expandedKeys = $state([]);
  // key -> { loading, holders } | { loading, holders: null, failed: true }
  let detailCache = $state({});

  // Epoch guard against stale-response races (same idiom as CardCollection's
  // loadToken): load() starts a new epoch; showMore() continues the current
  // one. Whichever response's token no longer matches loadToken is discarded,
  // regardless of which network call resolves last.
  let loadToken = 0;

  // Refetch page 1 whenever scope, sort, or filter change.
  $effect(() => {
    const key = JSON.stringify({ scopeParams, sort, q, section });
    void key;
    load();
  });

  async function load() {
    const token = ++loadToken;
    loading = true;
    failed = false;
    // A fresh load replaces the item set entirely; any expanded rows/cached
    // detail from the previous set are no longer valid (and their keys may
    // not even exist in the new set), so collapse everything rather than risk
    // a stale expand panel surviving a section reload.
    expandedKeys = [];
    detailCache = {};
    const res = await fetchBoardSection({ ...scopeParams, section, sort, q });
    if (token !== loadToken) return; // a newer load superseded this one
    if (res.ok) {
      items = (res.data.rows || []).map(boardRowToItem);
      nextCursor = res.data.next_cursor || null;
      if (res.data.counts) onCounts?.(res.data.counts);
    } else {
      items = [];
      nextCursor = null;
      failed = true;
    }
    loading = false;
  }

  async function showMore() {
    if (!nextCursor || loadingMore || loading) return;
    const token = loadToken; // continue the current epoch, don't start a new one
    loadingMore = true;
    const res = await fetchBoardSection({
      ...scopeParams,
      section,
      sort,
      q,
      cursor: nextCursor,
    });
    if (token === loadToken && res.ok) {
      items = [...items, ...(res.data.rows || []).map(boardRowToItem)];
      nextCursor = res.data.next_cursor || null;
    }
    loadingMore = false;
  }

  function toggleRow(item) {
    const key = item.key;
    if (expandedKeys.includes(key)) {
      expandedKeys = expandedKeys.filter((k) => k !== key);
      return;
    }
    expandedKeys = [...expandedKeys, key];
    // Re-expanding a row whose last fetch failed retries it (review finding).
    if (!detailCache[key] || detailCache[key].failed) loadDetail(item);
  }

  // Per-request identity for detail fetches: a boolean loading gate alone
  // can't tell a stale in-flight response from the CURRENT request when the
  // same card key is re-expanded after a reload (review finding). Each fetch
  // claims a token; only the response holding the key's current token applies.
  let detailToken = 0;

  async function loadDetail(item) {
    const key = item.key;
    const token = ++detailToken;
    detailCache = {
      ...detailCache,
      [key]: { loading: true, token, holders: null, failed: false },
    };
    const res = await fetchBoardDetail({ ...scopeParams, cardName: item.cardName });
    // Apply only if this key still exists (no reload wiped it) AND this
    // response belongs to the key's current request (no newer fetch claimed it).
    if (detailCache[key]?.token !== token) return;
    if (res.ok) {
      const yours = (res.data?.your_binders || []).map((b) => ({
        binderName: b.binder_name,
        binderUrl: b.binder_url,
        ownerName: 'You',
        quantity: b.quantity,
        price: b.price,
      }));
      const others = (res.data?.holders || []).map((h) => ({
        binderName: h.binder_name,
        binderUrl: h.binder_url,
        ownerName: h.owner_name,
        quantity: h.quantity,
        price: h.price,
      }));
      detailCache = {
        ...detailCache,
        [key]: { loading: false, holders: [...yours, ...others], failed: false },
      };
    } else {
      detailCache = { ...detailCache, [key]: { loading: false, holders: null, failed: true } };
    }
  }
</script>

<section class="wants-section">
  <h2 class="section-title">
    {title}
    {#if count != null}<span class="section-count">({count})</span>{/if}
  </h2>

  {#if loading}
    <p class="section-status">Loading…</p>
  {:else if failed}
    <p class="section-status">Couldn't load this section.</p>
  {:else if items.length === 0}
    <p class="section-status empty">Nothing here right now.</p>
  {:else}
    <div class="wants-list">
      {#each items as item (item.key)}
        {@const detail = detailCache[item.key]}
        <div class="wants-row-wrapper">
          <CardRow
            {item}
            {currencySymbol}
            onToggle={() => toggleRow(item)}
            onCardClick={() => onCardClick?.(item)}
          />
          {#if expandedKeys.includes(item.key)}
            <div class="wants-row-detail">
              {#if detail?.loading}
                <p class="detail-loading">Loading…</p>
              {:else if detail?.failed}
                <p class="no-holders">Couldn't load holders.</p>
              {:else if detail?.holders?.length}
                <CardHolders holders={detail.holders} {currencySymbol} />
              {:else}
                <p class="no-holders">No reachable binder has this.</p>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>
    {#if nextCursor}
      <button class="show-more" onclick={showMore} disabled={loadingMore}>
        {loadingMore ? 'Loading…' : 'Show more'}
      </button>
    {/if}
  {/if}
</section>

<style>
  .wants-section {
    margin-bottom: var(--space-xl);
  }
  .section-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--color-text);
    margin: 0 0 var(--space-sm);
    display: flex;
    align-items: baseline;
    gap: 8px;
  }
  .section-count {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--color-text-tertiary);
    font-family: var(--font-mono);
  }
  .section-status {
    font-size: 14px;
    color: var(--color-text-tertiary);
    padding: var(--space-sm) 0;
  }

  /* Replicates CardCollection.svelte's `.cc-list` wrapper so CardRow's grid
     (which consumes --cc-grid-cols) lays out identically to the search list.
     Kept in sync manually; see the note at the top of <script>. */
  .wants-list {
    --cc-grid-cols: minmax(200px, 2fr) 110px 80px 50px minmax(80px, 1fr) 100px;
    background: var(--color-bg-surface);
    border-radius: var(--radius-lg);
    overflow: hidden;
    border: 1px solid var(--color-border);
  }
  .wants-row-wrapper :global(.cc-row) {
    grid-template-columns: var(--cc-grid-cols);
  }
  /* CardRow's own :last-child border-removal only sees its single sibling
     inside our wrapper div, so it never fires; suppress the very last row's
     border explicitly to match search's borderless final row. */
  .wants-row-wrapper:last-child :global(.cc-row-wrapper) {
    border-bottom: none;
  }

  /* Mirrors CardRow's own .cc-row-detail styling (CardRow.svelte:249-258) so
     the expand panel matches search's when the row lives outside .cc-list. */
  .wants-row-detail {
    background: var(--color-bg);
    border-top: 1px solid var(--color-border);
    padding: 0.625rem 20px 0.625rem 50px;
  }
  .detail-loading,
  .no-holders {
    margin: 0;
    color: var(--color-text-tertiary);
    font-size: 0.8125rem;
  }

  .show-more {
    all: unset;
    cursor: pointer;
    display: block;
    margin: var(--space-sm) auto 0;
    padding: 8px 20px;
    font-size: 13px;
    font-family: var(--font-sans);
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
  }
  .show-more:hover {
    border-color: var(--color-text-muted);
  }
</style>
